/**
 * FireSafetyPage - Fire Equipment Register
 * 
 * Features:
 * - List all fire safety equipment with filtering
 * - Service schedule tracking with overdue alerts
 * - Compliance status indicators
 * - Add/edit fire equipment items
 * - Extinguisher type management
 */
import React, { useState, useEffect } from 'react';
import api, { formatDate } from '../lib/api';
import { FIRE_SAFETY_TYPES } from '../lib/constants';
import { Card, Spinner, EmptyState, Select } from '../components/shared';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Flame, Plus, MapPin, Calendar, CheckCircle, AlertCircle, X, Edit2, Clock } from 'lucide-react';

// Service status badge
const ServiceStatusBadge = ({ item }) => {
  const now = new Date();
  const nextService = item.next_service_at ? new Date(item.next_service_at) : null;
  
  if (!nextService) {
    return <span className="service-badge service-unknown">No Date</span>;
  }
  
  const daysUntil = Math.ceil((nextService - now) / (1000 * 60 * 60 * 24));
  
  if (daysUntil < 0) {
    return <span className="service-badge service-overdue" data-testid="service-overdue">Overdue ({Math.abs(daysUntil)}d)</span>;
  }
  if (daysUntil <= 30) {
    return <span className="service-badge service-due-soon" data-testid="service-due-soon">Due Soon ({daysUntil}d)</span>;
  }
  return <span className="service-badge service-ok" data-testid="service-ok">OK ({daysUntil}d)</span>;
};

// Condition badge
const ConditionBadge = ({ condition, compliant }) => {
  if (condition === 'NEEDS_SERVICE') {
    return <span className="condition-badge condition-warning">Needs Service</span>;
  }
  if (condition === 'FAULTY') {
    return <span className="condition-badge condition-danger">Faulty</span>;
  }
  if (!compliant) {
    return <span className="condition-badge condition-warning">Non-compliant</span>;
  }
  return <span className="condition-badge condition-ok">Serviceable</span>;
};

// Equipment type icon
const EquipmentTypeIcon = ({ type }) => {
  const config = FIRE_SAFETY_TYPES[type];
  if (config?.icon) {
    return <img src={config.icon} alt={type} className="equipment-type-icon" />;
  }
  return <Flame size={18} className="equipment-type-icon-svg" />;
};

// Add/Edit Modal
const FireSafetyFormModal = ({ isOpen, onClose, onSave, item, sites }) => {
  const [form, setForm] = useState({
    site_id: '',
    item_type: 'EXTINGUISHER',
    location: '',
    floor_level: '',
    asset_id: '',
    extinguisher_type: '',
    capacity: '',
    service_frequency_months: 6,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (item) {
      setForm({
        site_id: item.site_id || '',
        item_type: item.item_type || 'EXTINGUISHER',
        location: item.location || '',
        floor_level: item.floor_level || '',
        asset_id: item.asset_id || '',
        extinguisher_type: item.extinguisher_type || '',
        capacity: item.capacity || '',
        service_frequency_months: item.service_frequency_months || 6,
      });
    } else {
      setForm({
        site_id: sites[0]?.id || '',
        item_type: 'EXTINGUISHER',
        location: '',
        floor_level: '',
        asset_id: '',
        extinguisher_type: '',
        capacity: '',
        service_frequency_months: 6,
      });
    }
  }, [item, sites]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.location || !form.site_id) {
      toast.error('Please fill in required fields');
      return;
    }
    setSaving(true);
    try {
      await onSave(form);
      onClose();
    } catch (err) {
      toast.error(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const isExtinguisher = form.item_type === 'EXTINGUISHER';

  return (
    <div className="modal-overlay" data-testid="fire-safety-form-modal">
      <div className="modal-content modal-md">
        <div className="modal-header">
          <h2>{item ? 'Edit Fire Safety Item' : 'Add Fire Safety Item'}</h2>
          <button className="modal-close" onClick={onClose}><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label required">Site</label>
                <select 
                  className="form-select" 
                  value={form.site_id} 
                  onChange={(e) => setForm({...form, site_id: e.target.value})}
                  data-testid="fire-site-select"
                >
                  {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label required">Equipment Type</label>
                <select 
                  className="form-select" 
                  value={form.item_type}
                  onChange={(e) => setForm({...form, item_type: e.target.value})}
                  data-testid="fire-type-select"
                >
                  {Object.entries(FIRE_SAFETY_TYPES).map(([k, v]) => (
                    <option key={k} value={k}>{v.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-group form-group-full">
                <label className="form-label required">Location</label>
                <input 
                  type="text" 
                  className="form-input" 
                  placeholder="e.g. Level 1 - Reception"
                  value={form.location}
                  onChange={(e) => setForm({...form, location: e.target.value})}
                  data-testid="fire-location"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Floor Level</label>
                <input 
                  type="text" 
                  className="form-input" 
                  placeholder="e.g. Ground Floor"
                  value={form.floor_level}
                  onChange={(e) => setForm({...form, floor_level: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Asset ID</label>
                <input 
                  type="text" 
                  className="form-input" 
                  placeholder="e.g. FE-001"
                  value={form.asset_id}
                  onChange={(e) => setForm({...form, asset_id: e.target.value})}
                />
              </div>
              {isExtinguisher && (
                <>
                  <div className="form-group">
                    <label className="form-label">Extinguisher Type</label>
                    <select 
                      className="form-select" 
                      value={form.extinguisher_type}
                      onChange={(e) => setForm({...form, extinguisher_type: e.target.value})}
                      data-testid="fire-ext-type"
                    >
                      <option value="">Select...</option>
                      <option value="ABE">ABE (Dry Chemical)</option>
                      <option value="CO2">CO2 (Carbon Dioxide)</option>
                      <option value="WATER">Water</option>
                      <option value="FOAM">Foam</option>
                      <option value="WET_CHEM">Wet Chemical</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Capacity</label>
                    <input 
                      type="text" 
                      className="form-input" 
                      placeholder="e.g. 4.5kg"
                      value={form.capacity}
                      onChange={(e) => setForm({...form, capacity: e.target.value})}
                    />
                  </div>
                </>
              )}
              <div className="form-group">
                <label className="form-label">Service Frequency</label>
                <select 
                  className="form-select" 
                  value={form.service_frequency_months}
                  onChange={(e) => setForm({...form, service_frequency_months: parseInt(e.target.value)})}
                >
                  <option value={3}>Every 3 months</option>
                  <option value={6}>Every 6 months</option>
                  <option value={12}>Every 12 months</option>
                  <option value={24}>Every 2 years</option>
                  <option value={60}>Every 5 years</option>
                </select>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" variant="primary" disabled={saving} data-testid="fire-save-btn">
              {saving ? 'Saving...' : (item ? 'Update' : 'Add Item')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Detail Modal
const FireSafetyDetailModal = ({ isOpen, onClose, item, onEdit, onMarkServiced }) => {
  if (!isOpen || !item) return null;

  const typeConfig = FIRE_SAFETY_TYPES[item.item_type] || { label: item.item_type };

  return (
    <div className="modal-overlay" data-testid="fire-safety-detail-modal">
      <div className="modal-content modal-md">
        <div className="modal-header">
          <div className="modal-header-content">
            <EquipmentTypeIcon type={item.item_type} />
            <h2>{typeConfig.label}</h2>
          </div>
          <button className="modal-close" onClick={onClose}><X size={20} /></button>
        </div>
        <div className="modal-body">
          <div className="detail-grid">
            <div className="detail-section">
              <h3>Location</h3>
              <div className="detail-row">
                <span className="detail-label">Site</span>
                <span className="detail-value">{item.site_name}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Location</span>
                <span className="detail-value">{item.location}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Floor</span>
                <span className="detail-value">{item.floor_level || 'N/A'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Asset ID</span>
                <span className="detail-value">{item.asset_id || 'N/A'}</span>
              </div>
            </div>
            
            {item.item_type === 'EXTINGUISHER' && (
              <div className="detail-section">
                <h3>Extinguisher Details</h3>
                <div className="detail-row">
                  <span className="detail-label">Type</span>
                  <span className="detail-value">{item.extinguisher_type || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Capacity</span>
                  <span className="detail-value">{item.capacity || 'N/A'}</span>
                </div>
              </div>
            )}

            <div className="detail-section">
              <h3>Service Status</h3>
              <div className="detail-row">
                <span className="detail-label">Next Service</span>
                <span className="detail-value">{item.next_service_at ? formatDate(item.next_service_at) : 'Not set'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Service Status</span>
                <ServiceStatusBadge item={item} />
              </div>
              <div className="detail-row">
                <span className="detail-label">Condition</span>
                <ConditionBadge condition={item.condition} compliant={item.compliant} />
              </div>
              <div className="detail-row">
                <span className="detail-label">Frequency</span>
                <span className="detail-value">Every {item.service_frequency_months || 6} months</span>
              </div>
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <Button variant="ghost" onClick={onClose}>Close</Button>
          <Button variant="outline" onClick={() => onMarkServiced(item)} data-testid="fire-mark-serviced">
            <CheckCircle size={16} /> Mark Serviced
          </Button>
          <Button variant="primary" onClick={() => onEdit(item)} data-testid="fire-edit-btn">
            <Edit2 size={16} /> Edit
          </Button>
        </div>
      </div>
    </div>
  );
};

// Main Page Component
const FireSafetyPage = () => {
  const [items, setItems] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ site_id: '', item_type: '' });
  const [showFormModal, setShowFormModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  useEffect(() => {
    loadData();
  }, [filters]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load sites
      const sitesRes = await api.get('/sites');
      setSites(sitesRes.items || []);

      // Load fire safety items
      let url = '/fire-safety';
      const params = new URLSearchParams();
      if (filters.site_id) params.append('site_id', filters.site_id);
      if (filters.item_type) params.append('item_type', filters.item_type);
      if (params.toString()) url += `?${params.toString()}`;
      
      const res = await api.get(url);
      setItems(res.items || []);
    } catch (err) {
      console.error('Failed to load fire safety items:', err);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (formData) => {
    if (selectedItem) {
      await api.patch(`/fire-safety/${selectedItem.id}`, formData);
      toast.success('Fire safety item updated');
    } else {
      await api.post('/fire-safety', formData);
      toast.success('Fire safety item added');
    }
    loadData();
    setSelectedItem(null);
  };

  const handleMarkServiced = async (item) => {
    try {
      // Calculate new service date based on frequency
      const nextServiceDate = new Date();
      nextServiceDate.setMonth(nextServiceDate.getMonth() + (item.service_frequency_months || 6));
      
      await api.patch(`/fire-safety/${item.id}`, {
        next_service_at: nextServiceDate.toISOString(),
        condition: 'SERVICEABLE',
        compliant: true,
      });
      toast.success('Item marked as serviced');
      setShowDetailModal(false);
      loadData();
    } catch (err) {
      toast.error('Failed to update service status');
    }
  };

  const handleAdd = () => {
    setSelectedItem(null);
    setShowFormModal(true);
  };

  const handleEdit = (item) => {
    setSelectedItem(item);
    setShowDetailModal(false);
    setShowFormModal(true);
  };

  const handleView = (item) => {
    setSelectedItem(item);
    setShowDetailModal(true);
  };

  // Calculate summary stats
  const now = new Date();
  const stats = {
    total: items.length,
    overdue: items.filter(item => {
      const nextService = item.next_service_at ? new Date(item.next_service_at) : null;
      return nextService && nextService < now;
    }).length,
    dueSoon: items.filter(item => {
      const nextService = item.next_service_at ? new Date(item.next_service_at) : null;
      if (!nextService || nextService < now) return false;
      const daysUntil = Math.ceil((nextService - now) / (1000 * 60 * 60 * 24));
      return daysUntil <= 30;
    }).length,
    nonCompliant: items.filter(item => !item.compliant).length,
  };

  return (
    <div className="page fire-safety-page" data-testid="fire-safety-page">
      <div className="page-header">
        <div>
          <h1>Fire Safety Register</h1>
          <p>Track fire equipment, service schedules, and compliance</p>
        </div>
        <Button variant="primary" onClick={handleAdd} data-testid="add-fire-safety-btn">
          <Plus size={16} /> Add Equipment
        </Button>
      </div>

      {/* Summary Stats */}
      <div className="page-stats" data-testid="fire-safety-stats">
        <div className="stat-card">
          <Flame size={20} className="stat-icon" />
          <div className="stat-content">
            <span className="stat-value">{stats.total}</span>
            <span className="stat-label">Total Items</span>
          </div>
        </div>
        <div className={`stat-card ${stats.overdue > 0 ? 'stat-danger' : ''}`}>
          <AlertCircle size={20} className="stat-icon" />
          <div className="stat-content">
            <span className="stat-value">{stats.overdue}</span>
            <span className="stat-label">Overdue</span>
          </div>
        </div>
        <div className={`stat-card ${stats.dueSoon > 0 ? 'stat-warning' : ''}`}>
          <Clock size={20} className="stat-icon" />
          <div className="stat-content">
            <span className="stat-value">{stats.dueSoon}</span>
            <span className="stat-label">Due Soon</span>
          </div>
        </div>
        <div className={`stat-card ${stats.nonCompliant > 0 ? 'stat-danger' : ''}`}>
          <CheckCircle size={20} className="stat-icon" />
          <div className="stat-content">
            <span className="stat-value">{stats.total - stats.nonCompliant}</span>
            <span className="stat-label">Compliant</span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <Card className="filters-card">
        <div className="filters-row">
          <Select
            label="Site"
            value={filters.site_id}
            onChange={(e) => setFilters({ ...filters, site_id: e.target.value })}
            options={[
              { value: '', label: 'All Sites' },
              ...sites.map(s => ({ value: s.id, label: s.name }))
            ]}
          />
          <Select
            label="Equipment Type"
            value={filters.item_type}
            onChange={(e) => setFilters({ ...filters, item_type: e.target.value })}
            options={[
              { value: '', label: 'All Types' },
              ...Object.entries(FIRE_SAFETY_TYPES).map(([k, v]) => ({ value: k, label: v.label }))
            ]}
          />
          {(filters.site_id || filters.item_type) && (
            <button className="filter-clear-btn" onClick={() => setFilters({ site_id: '', item_type: '' })}>
              Clear filters
            </button>
          )}
        </div>
      </Card>

      {/* Items List */}
      {loading ? (
        <div className="page-loading"><Spinner /></div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={<Flame size={48} />}
          title="No fire safety equipment found"
          description={filters.site_id || filters.item_type ? "No items match the current filters" : "Add your first fire safety item to get started"}
        />
      ) : (
        <Card className="table-card">
          <table className="data-table" data-testid="fire-safety-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Location</th>
                <th>Site</th>
                <th>Asset ID</th>
                <th>Service Due</th>
                <th>Condition</th>
              </tr>
            </thead>
            <tbody>
              {items.map(item => (
                <tr 
                  key={item.id} 
                  onClick={() => handleView(item)}
                  className="table-row clickable"
                  data-testid={`fire-row-${item.id}`}
                >
                  <td>
                    <div className="equipment-type-cell">
                      <EquipmentTypeIcon type={item.item_type} />
                      <span>{FIRE_SAFETY_TYPES[item.item_type]?.label || item.item_type}</span>
                    </div>
                  </td>
                  <td className="cell-location">
                    <MapPin size={14} />
                    <span>{item.location}</span>
                  </td>
                  <td>{item.site_name}</td>
                  <td><code>{item.asset_id || '-'}</code></td>
                  <td><ServiceStatusBadge item={item} /></td>
                  <td><ConditionBadge condition={item.condition} compliant={item.compliant} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* Modals */}
      <FireSafetyFormModal
        isOpen={showFormModal}
        onClose={() => { setShowFormModal(false); setSelectedItem(null); }}
        onSave={handleSave}
        item={selectedItem}
        sites={sites}
      />
      <FireSafetyDetailModal
        isOpen={showDetailModal}
        onClose={() => { setShowDetailModal(false); setSelectedItem(null); }}
        item={selectedItem}
        onEdit={handleEdit}
        onMarkServiced={handleMarkServiced}
      />
    </div>
  );
};

export default FireSafetyPage;
